import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CharterPermit, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const REGISTRAR_ROLES = ['SHIP_REGISTRAR'];
const STATUSES = ['APPLIED', 'UNDER_REVIEW', 'FEE_PENDING', 'ACTIVE', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'REJECTED'];

/** M22 — charter yacht/vessel operating permits: itinerary, pax capacity, insurance, safety cert chain. */
export function CharterPermits() {
  const { persona } = useAuth();
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showApply, setShowApply] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m22/charter-permits?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<CharterPermit>>(path);

  return (
    <div>
      <PageHeader
        title="Charter Permits"
        subtitle="Charter yacht/vessel operating permits on FULL_REGISTRATION vessels — itinerary, capacity, insurance (M22 — SMSA)."
        actions={
          <>
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            {isRegistrar ? (
              <button className="btn-primary" onClick={() => setShowApply(true)}>
                + Apply for permit
              </button>
            ) : null}
          </>
        }
      />

      {!isRegistrar ? <RoleNotice roles={REGISTRAR_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No charter permits match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Permit no.</th>
                <th className="table-head">Operator</th>
                <th className="table-head">Pax capacity</th>
                <th className="table-head">Valid to</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{p.permitNo}</td>
                  <td className="table-cell font-mono text-xs">{p.operatorId}</td>
                  <td className="table-cell text-xs text-slate-500">{p.paxCapacity}</td>
                  <td className="table-cell text-xs text-slate-500">{p.validTo ? formatDate(p.validTo) : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showApply ? (
        <ApplyCharterPermitModal
          onClose={() => setShowApply(false)}
          onCreated={() => {
            setShowApply(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <CharterPermitDetailModal
          id={selectedId}
          canAct={isRegistrar}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function ApplyCharterPermitModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [vesselRegistrationId, setVesselRegistrationId] = useState('');
  const [operatorType, setOperatorType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [operatorId, setOperatorId] = useState('');
  const [itinerary, setItinerary] = useState('');
  const [paxCapacity, setPaxCapacity] = useState('');
  const [insurancePolicyRef, setInsurancePolicyRef] = useState('');
  const [insuranceExpiresAt, setInsuranceExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m22/charter-permits', {
        vesselRegistrationId: vesselRegistrationId.trim(),
        operatorType,
        operatorId: operatorId.trim(),
        itinerary: itinerary.trim(),
        paxCapacity: Number.parseInt(paxCapacity, 10),
        insurancePolicyRef: insurancePolicyRef.trim(),
        insuranceExpiresAt: new Date(insuranceExpiresAt).toISOString(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to apply for permit');
    } finally {
      setBusy(false);
    }
  };

  const valid = vesselRegistrationId.trim() && operatorId.trim() && itinerary.trim().length >= 3 && paxCapacity && insurancePolicyRef.trim() && insuranceExpiresAt;

  return (
    <Modal title="Apply for a charter permit" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <p className="mb-3 text-xs text-slate-500">Vessel must already be FULL_REGISTRATION — find its id in the Vessel Registry.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Vessel registration id" className="col-span-2">
          <input className="field-input" value={vesselRegistrationId} onChange={(e) => setVesselRegistrationId(e.target.value)} />
        </Field>
        <Field label="Operator type">
          <select className="field-input" value={operatorType} onChange={(e) => setOperatorType(e.target.value as 'PERSON' | 'ENTITY')}>
            <option value="ENTITY">Entity</option>
            <option value="PERSON">Person</option>
          </select>
        </Field>
        <Field label="Operator id">
          <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
        </Field>
        <Field label="Itinerary" className="col-span-2">
          <textarea className="field-input" rows={2} value={itinerary} onChange={(e) => setItinerary(e.target.value)} />
        </Field>
        <Field label="Pax capacity">
          <input className="field-input" type="number" value={paxCapacity} onChange={(e) => setPaxCapacity(e.target.value)} />
        </Field>
        <Field label="Insurance policy ref">
          <input className="field-input" value={insurancePolicyRef} onChange={(e) => setInsurancePolicyRef(e.target.value)} />
        </Field>
        <Field label="Insurance expires">
          <input className="field-input" type="date" value={insuranceExpiresAt} onChange={(e) => setInsuranceExpiresAt(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Applying…' : 'Submit application'}
      </button>
    </Modal>
  );
}

function CharterPermitDetailModal({ id, canAct, onClose, onChanged }: { id: string; canAct: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<CharterPermit>(`/api/v1/m22/charter-permits/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [reason, setReason] = useState('');
  const [showAction, setShowAction] = useState<'suspend' | 'revoke' | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const p = q.data;

  return (
    <Modal title={p ? `Charter permit ${p.permitNo}` : 'Charter permit'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Operator" value={p.operatorId} mono />
            <Detail label="Pax capacity" value={String(p.paxCapacity)} />
            <Detail label="Insurance ref" value={p.insurancePolicyRef} />
            <Detail label="Valid from" value={p.validFrom ? formatDate(p.validFrom) : '—'} />
            <Detail label="Valid to" value={p.validTo ? formatDate(p.validTo) : '—'} />
          </div>
          <Detail label="Itinerary" value={p.itinerary} />
          {p.rejectionReason ? <ErrorNote message={`Rejected: ${p.rejectionReason}`} /> : null}
          {p.suspendedReason ? <ErrorNote message={`Suspended: ${p.suspendedReason}`} /> : null}

          {canAct ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              {p.status === 'APPLIED' ? (
                <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m22/charter-permits/${p.id}/review`))}>
                  {busy ? 'Moving…' : 'Move to review'}
                </button>
              ) : null}

              {p.status === 'UNDER_REVIEW' ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button className={`btn-success ${decision === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('APPROVE')}>
                      Approve
                    </button>
                    <button className={`btn-danger ${decision === 'REJECT' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('REJECT')}>
                      Reject
                    </button>
                  </div>
                  {decision ? (
                    <>
                      <Field label="Reason">
                        <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                      </Field>
                      <button
                        className={decision === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                        disabled={busy || reason.trim().length < 3}
                        onClick={() => call(() => api.post(`/api/v1/m22/charter-permits/${p.id}/decide`, { decision, reason: reason.trim() }))}
                      >
                        {busy ? 'Submitting…' : `Confirm ${decision.toLowerCase()}`}
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}

              {p.status === 'ACTIVE' ? (
                showAction === 'suspend' ? (
                  <div className="space-y-2">
                    <Field label="Suspension reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className="btn-danger"
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m22/charter-permits/${p.id}/suspend`, { reason: reason.trim() }))}
                    >
                      Confirm suspend
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('suspend')}>
                    Suspend
                  </button>
                )
              ) : null}

              {p.status === 'SUSPENDED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m22/charter-permits/${p.id}/reinstate`))}>
                  {busy ? 'Reinstating…' : 'Reinstate'}
                </button>
              ) : null}

              {p.status === 'ACTIVE' || p.status === 'SUSPENDED' ? (
                showAction === 'revoke' ? (
                  <div className="space-y-2">
                    <Field label="Revocation reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className="btn-danger"
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m22/charter-permits/${p.id}/revoke`, { reason: reason.trim() }))}
                    >
                      Confirm revoke
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('revoke')}>
                    Revoke
                  </button>
                )
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={REGISTRAR_ROLES} />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
